import React, { useState, useEffect } from "react";
import { Header } from "../../Header";
import './PypOrders.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';

// Level 3 Component (Sales inside Planned Orders)
function PlannedOrderRow(props) {
    const { order } = props;
    const [open, setOpen] = useState(false);

    const hasSales = order.sales && order.sales.length > 0;

    return (
        <React.Fragment>
            <TableRow>
                <TableCell style={{ width: '40px', padding: '0 8px' }}>
                    {hasSales && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{order.SaleOrderID}</TableCell>
                <TableCell>{order.DeliveryScheduleDate ? dayjs(order.DeliveryScheduleDate).format('DD-MM-YYYY') : '-'}</TableCell>
            </TableRow>
            {hasSales && (
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box className="pyporders-level3-area">
                                <Typography variant="subtitle2" style={{ color: '#004D00', fontWeight: 700, marginBottom: '5px', fontSize: '11px' }}>
                                    Sales Details
                                </Typography>
                                <Table size="small" className="pyporders-level3-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Branch ID</TableCell>
                                            <TableCell>Sale ID</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Is Returned</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.sales.map((sale) => (
                                            <TableRow key={`${sale.Branchid}-${sale.saleid}`}>
                                                <TableCell>{sale.Branchid}</TableCell>
                                                <TableCell>{sale.saleid}</TableCell>
                                                <TableCell>{sale.DeliveryStatusCode}</TableCell>
                                                <TableCell>{sale.IsReturnedSale ? 'Yes' : 'No'}</TableCell>
                                                <TableCell align="right" style={{ fontWeight: 600, color: '#1b5e20' }}>
                                                    {sale.TotalAmount}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
}

// Level 1 Component (Master Row expanding to Collections and Planned Orders)
function MasterRow(props) {
    const { row } = props;
    const [open, setOpen] = useState(false);

    const hasCollections = row.collect && row.collect.length > 0;
    const hasPlannedOrders = row.plannedorders && row.plannedorders.length > 0;
    const canExpand = hasCollections || hasPlannedOrders;

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} className="pyporders-row-main">
                <TableCell>
                    {canExpand && (
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{row.SFAreaName || '-'}</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>{row.ParentSaleOrderID}</TableCell>
                <TableCell>
                    <div style={{ fontWeight: 600, color: '#333' }}>{row.CustomerName || '-'}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>{row.Mobile || '-'}</div>
                </TableCell>
                <TableCell>{row.ChannelID || '-'}</TableCell>
                <TableCell>{row.OrderDate ? dayjs(row.OrderDate).format('DD-MM-YYYY HH:mm') : '-'}</TableCell>
                <TableCell>
                    <div className="pyporders-status-group">
                        {row.PendingOrders > 0 && <span className="pyporder-chip pending">{row.PendingOrders} Pend</span>}
                        {row.CancelledOrders > 0 && <span className="pyporder-chip cancelled">{row.CancelledOrders} Canc</span>}
                        {row.Postponedorders > 0 && <span className="pyporder-chip postponed">{row.Postponedorders} Post</span>}
                        {(row.PendingOrders === 0 && row.CancelledOrders === 0 && row.Postponedorders === 0) && '-'}
                    </div>
                </TableCell>
                <TableCell className="pyporders-currency" align="right">{row.TotalAmount}</TableCell>
            </TableRow>
            
            {canExpand && (
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box className="pyporders-expanded-area">
                                
                                {hasCollections && (
                                    <>
                                        <Typography className="pyporders-section-title">Collections</Typography>
                                        <Table size="small" className="pyporders-level2-table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Branch ID</TableCell>
                                                    <TableCell>Collection ID</TableCell>
                                                    <TableCell>Payment Method</TableCell>
                                                    <TableCell>Gateway</TableCell>
                                                    <TableCell>Ref ID</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {row.collect.map((col) => (
                                                    <TableRow key={col.CollectionId}>
                                                        <TableCell>{col.CollectionBranchId}</TableCell>
                                                        <TableCell>{col.CollectionId}</TableCell>
                                                        <TableCell>{col.PaymentMethodId}</TableCell>
                                                        <TableCell>{col.PaymentGatewayName}</TableCell>
                                                        <TableCell>{col.PaymentRef || '-'}</TableCell>
                                                        <TableCell align="right" style={{ fontWeight: 600 }}>{col.Amount}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </>
                                )}

                                {hasPlannedOrders && (
                                    <>
                                        <Typography className="pyporders-section-title">Planned Orders</Typography>
                                        <Table size="small" className="pyporders-level2-table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ width: '40px' }} />
                                                    <TableCell>Planned Order ID</TableCell>
                                                    <TableCell>Delivery Schedule Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {row.plannedorders.map((po) => (
                                                    <PlannedOrderRow key={po.SaleOrderID} order={po} />
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </>
                                )}

                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
}

export const PypOrders = () => {
    const token = localStorage.getItem('AuthorizationToken');
    
    const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
    const [toDate, setToDate] = useState(dayjs().add(10, 'day'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [filterText, setFilterText] = useState('');

    const handleAuthError = () => {
        setError("Authentication error. Please log in again.");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    const fetchReportData = async () => {
        if (!fromDate || !toDate) {
            setError('Please select both Start Date and End Date.');
            return;
        }

        if (fromDate.isAfter(toDate)) {
            setError('Start Date cannot be after End Date.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const startDateStr = fromDate.format('YYYYMMDD');
            const endDateStr = toDate.format('YYYYMMDD');

            const url = `https://tpapi.abisaio.com:3060/api/v1/reportjsonpyporders?AreaID=ALL&CustId=ALL&StartDate=${startDateStr}&EndDate=${endDateStr}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleAuthError();
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const jsonResponse = await response.json();
            
            if (jsonResponse && Array.isArray(jsonResponse.PYPOrders)) {
                setReportData(jsonResponse.PYPOrders);
                if (jsonResponse.PYPOrders.length === 0) {
                    setError("No data available for the selected filters.");
                }
            } else {
                console.error("Unexpected data format:", jsonResponse);
                setError("Received invalid data format from server.");
                setReportData([]);
            }

        } catch (err) {
            console.error("Error fetching PYP orders report data:", err);
            setError(`Failed to fetch report data. Please check your connection. (${err.message})`);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = reportData.filter(item => {
        if (!filterText) return true;
        const searchText = filterText.toLowerCase();
        return (
            (item.ParentSaleOrderID && item.ParentSaleOrderID.toLowerCase().includes(searchText)) ||
            (item.CustomerName && item.CustomerName.toLowerCase().includes(searchText)) ||
            (item.Mobile && item.Mobile.toLowerCase().includes(searchText))
        );
    });

    const exportToExcel = () => {
        if (filteredData.length === 0) {
            setError("No data to export.");
            return;
        }

        try {
            const flattenedData = [];

            filteredData.forEach(master => {
                const baseRow = {
                    'Area Name': master.SFAreaName || '',
                    'Parent Order ID': master.ParentSaleOrderID || '',
                    'Customer Name': master.CustomerName ? master.CustomerName.trim() : '',
                    'Mobile': master.Mobile || '',
                    'Channel': master.ChannelID || '',
                    'Order Date': master.OrderDate ? dayjs(master.OrderDate).format('DD-MM-YYYY HH:mm:ss') : '',
                    'Total Amount': master.TotalAmount || 0,
                    'Total Orders': master.TotalOrders || 0,
                    'Delivered Orders': master.DeleiverdOrders || 0,
                    'Pending Orders': master.PendingOrders || 0,
                    'Postponed Orders': master.PostponedOrders || 0,
                    'Cancelled Orders': master.CancelledOrders || 0,
                    'Delivered Value': master.DeleiverdValue || 0,
                    'Pending Value': master.PendingValue || 0,
                    'Postponed Value': master.PostponedValue || 0,
                    'Cancelled Value': master.CancelledValue || 0,
                    'Refundable Value': master.RefundableValue || 0,
                    'Refund Processed': master.RefundProcessed || 0,
                };

                // The master has Collections and Planned Orders. We want to align this gracefully.
                // We will create rows based on the combination of collections and planned orders.
                // Usually, Collection info and Order info happen independently, so we'll 
                // pair collections with the first few rows, and planned orders with the others, OR
                // cross join them. To stick to standard reports without combinatorial explosion,
                // we'll zip them (Output all collections, output all planned orders).
                // Actually, an elegant flatten: 
                // For a master row, if it has 2 collections and 3 sales, we output max(2, 3) rows. 
                
                const collections = master.collect || [];
                // Flatten planned orders and sales -> an array of "Sale Items"
                const saleItems = [];
                if (master.plannedorders && master.plannedorders.length > 0) {
                    master.plannedorders.forEach(po => {
                        const poBase = {
                            'Planned Order ID': po.SaleOrderID || '',
                            'Delivery Date': po.DeliveryScheduleDate ? dayjs(po.DeliveryScheduleDate).format('DD-MM-YYYY') : ''
                        };

                        if (po.sales && po.sales.length > 0) {
                            po.sales.forEach(sale => {
                                saleItems.push({
                                    ...poBase,
                                    'Sale Branch': sale.Branchid || '',
                                    'Sale ID': sale.saleid || '',
                                    'Sale Status': sale.DeliveryStatusCode || '',
                                    'Sale SubTotal': sale.TotalAmount || 0,
                                    'Is Returned': sale.IsReturnedSale ? 'Yes' : 'No'
                                });
                            });
                        } else {
                            saleItems.push({
                                ...poBase,
                                'Sale Branch': '', 'Sale ID': '', 'Sale Status': '', 'Sale SubTotal': 0, 'Is Returned': ''
                            });
                        }
                    });
                }
                
                // If it has NO collections and NO planned orders, just push the base row.
                if (collections.length === 0 && saleItems.length === 0) {
                    flattenedData.push({
                        ...baseRow,
                        'Collection ID': '', 'Collection Gateway': '', 'Collection Amount': 0,
                        'Planned Order ID': '', 'Delivery Date': '',
                        'Sale Branch': '', 'Sale ID': '', 'Sale Status': '', 'Sale SubTotal': 0, 'Is Returned': ''
                    });
                } else {
                    // Output side-by-side by index
                    const maxRows = Math.max(collections.length, saleItems.length);
                    for (let i = 0; i < maxRows; i++) {
                        const col = collections[i] || {};
                        const sale = saleItems[i] || {};

                        // We only want to print baseRow info explicitly on the very FIRST row to avoid clutter,
                        // OR we print it on all rows to allow filtering (standard for Excel data models).
                        // Let's print it on ALL rows for filtering capability.
                        
                        flattenedData.push({
                            ...baseRow,
                            'Collection Sub-Branch': col.CollectionBranchId || '',
                            'Collection ID': col.CollectionId || '',
                            'Collection Method': col.PaymentMethodId || '',
                            'Collection Gateway': col.PaymentGatewayName || '',
                            'Collection Ref': col.PaymentRef || '',
                            'Collection Amount': col.Amount || 0,
                            ...sale // Spread the sale object which contains PlannedOrder and specific sales params
                        });
                    }
                }
            });

            const worksheet = XLSX.utils.json_to_sheet(flattenedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "PYP Orders Data");

            const fileName = `PYP_Orders_Advanced_${dayjs().format('DDMMYYYY')}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } catch (err) {
            console.error("Error exporting Excel file:", err);
            setError("Failed to export Excel file.");
        }
    };

    const Loader = () => (
        <div className="pyporders-loader-container">
            <div className="pyporders-loader"></div>
            <p className="pyporders-loader-text">Loading Complex Data Structure...</p>
        </div>
    );

    return (
        <>
            <Header />
            <div className="pyporders-container">
                {loading && <Loader />}
                
                <h2 className="pyporders-title">PYP Orders Report</h2>

                <div className="pyporders-controls">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Start Date"
                            value={fromDate}
                            onChange={(newValue) => setFromDate(newValue)}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                        />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="End Date"
                            value={toDate}
                            onChange={(newValue) => setToDate(newValue)}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                        />
                    </LocalizationProvider>

                    <div className="pyporders-search">
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="Search Order ID, Customer, Mobile..."
                            className="pyporders-search-field"
                        />
                    </div>

                    <div className="pyporders-buttons">
                        <button onClick={fetchReportData} className="pyporders-button pyporders-load-button" disabled={loading}>
                            Fetch Report
                        </button>
                        <button onClick={exportToExcel} className="pyporders-button pyporders-export-button" disabled={loading || filteredData.length === 0}>
                            Excel Export
                        </button>
                    </div>
                </div>

                {error && <div className="pyporders-error-message">{error}</div>}

                <TableContainer component={Paper} className="pyporders-table-container" sx={{ maxHeight: 'calc(100vh - 270px)' }}>
                    <Table stickyHeader aria-label="collapsible master table" className="pyporders-report">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '40px' }} />
                                <TableCell>Area</TableCell>
                                <TableCell>Parent Order ID</TableCell>
                                <TableCell>Customer Info</TableCell>
                                <TableCell>Channel</TableCell>
                                <TableCell>Order Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Total Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.length > 0 ? (
                                filteredData.map((row) => (
                                    <MasterRow key={row.ParentSaleOrderID} row={row} />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" style={{ padding: '40px', color: '#666', fontStyle: 'italic' }}>
                                        {reportData.length === 0 && !error 
                                            ? "Select dates and click 'Fetch Report' to load data" 
                                            : "No data available matching filters"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </>
    );
};
